import { useEffect, useState } from "react"
import { Calendar, dateFnsLocalizer } from "react-big-calendar"
import format from "date-fns/format"
import parse from "date-fns/parse"
import startOfWeek from "date-fns/startOfWeek"
import getDay from "date-fns/getDay"
import "react-big-calendar/lib/css/react-big-calendar.css"
import { useApiService } from "../hooks/Api/useApiService"
import { addHours, addDays, addWeeks } from "date-fns"
import "./SharieCalendar.css"

import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  DialogActions,
  Button,
  Box,
  Divider,
} from "@mui/material"
import InfoIcon from "@mui/icons-material/Info"

// Date-fns locales
const locales = {
  "en-US": require("date-fns/locale/en-US"),
}

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
})

const SharieCalendar = () => {
  const [meetings, setMeetings] = useState([])
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const { postData } = useApiService()

  useEffect(() => {
    postData("sharie/getmeetings", { user_name: localStorage.getItem("user") })
      .then((data) => {
        const events = (data?.meetings || []).filter((event) => event.date_time)

        const formattedEvents = events.map((event) => {
          const start = parseCustomDate(event.date_time)
          return {
            ...event,
            title: `${event.name} - ${event.to}`,
            start,
            end: new Date(calculateEndTime(start, event.duration)),
            allDay: false,
          }
        })

        setMeetings(formattedEvents)
      })
      .catch((error) => {
        console.error("Error fetching events:", error)
      })
  }, [])

  function parseCustomDate(dateStr) {
    if (!dateStr || typeof dateStr !== "string") return new Date()

    try {
      const [datePart, timePart] = dateStr.split(", ")
      const [day, month, year] = datePart.split(".").map(Number)
      const [hours, minutes, seconds] = timePart.split(":").map(Number)
      return new Date(year, month - 1, day, hours, minutes, seconds)
    } catch (err) {
      console.error("Failed to parse date:", dateStr, err)
      return new Date()
    }
  }

  function calculateEndTime(start, durationText) {
    if (!durationText || typeof durationText !== "string") {
      return addHours(start, 1)
    }

    const lower = durationText.toLowerCase()
    const match = lower.match(/(\d+)\s*(hour|hours|day|days|week|weeks)/)

    if (!match) return addHours(start, 1)

    const value = parseInt(match[1])
    const unit = match[2]

    switch (unit) {
      case "hour":
      case "hours":
        return addHours(start, value)
      case "day":
      case "days":
        return addDays(start, value)
      case "week":
      case "weeks":
        return addWeeks(start, value)
      default:
        return addHours(start, 1)
    }
  }

  const handleSelectEvent = (event) => {
    setSelectedEvent(event)
    setDialogOpen(true)
  }

  return (
    <div style={{ height: "600px", padding: "1rem" }}>
      <Calendar
        localizer={localizer}
        events={meetings}
        startAccessor='start'
        endAccessor='end'
        style={{ height: "100%" }}
        onSelectEvent={handleSelectEvent}
      />

      {/* Dialog popup for selected event */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 2,
            bgcolor: "background.paper",
            boxShadow: 8,
            minWidth: 360,
          },
        }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, color: "primary.main" }}>
          <InfoIcon color='primary' />
          <Typography variant='h6' component='div' fontWeight='bold'>
            Meeting Details
          </Typography>
        </DialogTitle>

        <Divider />

        <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <DetailItem label='Name' value={selectedEvent?.name} />
          <DetailItem label='From' value={selectedEvent?.from} />
          <DetailItem label='To' value={selectedEvent?.to} />
          <DetailItem label='Start' value={selectedEvent?.start?.toLocaleString()} />
          <DetailItem label='End' value={selectedEvent?.end?.toLocaleString()} />
          <DetailItem label='Duration' value={selectedEvent?.duration || "1 Hour"} />
          <DetailItem label='Description' value={selectedEvent?.name || "N/A"} />
        </DialogContent>

        <DialogActions>
          <Button variant='outlined' color='primary' onClick={() => setDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}
const DetailItem = ({ label, value }) => (
  <Box>
    <Typography variant='subtitle2' color='text.secondary'>
      {label}
    </Typography>
    <Typography variant='body1' fontWeight='500'>
      {value}
    </Typography>
  </Box>
)
export default SharieCalendar
